import { ExpandMore } from "@mui/icons-material";
import { Button, MenuItem, Slider, TextField, Popover } from "@mui/material";
import { useEffect, useState } from "react";

import useService from "../../../../../../hooks/useService";
import { onScrollDown } from "../../../../../ui library/helpers/functions";
import HNSColors from "../../../../../ui library/HNSColors";
import sharedStyles from "../../modules/DynamicSection/styles/formStyles";
import QuestionTitle from "../QuestionTitle";

import NoteModal from "./components/NoteModal";

const Injuries = ({ label, data, schema, sectionId, questionKey, handleAnswerChange }) => {
  const fieldOptionRepository = useService("repositories/field-option-repository");
  const [formData, setFormData] = useState(data?.injuries ?? []);
  const [initialInjuries, setInitialInjuries] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [options, setOptions] = useState([]);
  const [openNotesModal, setOpenNotesModal] = useState(false);
  const [selectedInjury, setSelectedInjury] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const openMenuOpts = Boolean(anchorEl);

  function handleAdd(body_part) {
    const newInjury = { body_part, pain_level: 0, pain_description: "" };
    const updatedData = [...formData, newInjury];
    setFormData(updatedData);
    handleAnswerChange(questionKey, {
      injuries: updatedData,
    });
    handleCloseOpts();
    onScrollDown(sectionId);
  }

  function handleChange(body_part, pain_level) {
    const updatedData = formData.map((item) =>
      item.body_part === body_part ? { ...item, pain_level } : item
    );
    setFormData(updatedData);
    handleAnswerChange(questionKey, {
      injuries: updatedData,
    });
  }

  function renderColor(painLevel) {
    // Mid
    if (painLevel > 3 && painLevel < 8) {
      return "#F9C646";
    }
    // Bad
    if (painLevel >= 8) {
      return HNSColors.failure[500];
    }
    // Ok
    return HNSColors.success[500];
  }

  function handleCloseOpts() {
    setAnchorEl(null);
    setSearchTerm("");
  }

  function handleCloseModal() {
    setSelectedInjury(null);
    setOpenNotesModal(false);
  }

  useEffect(() => {
    setFormData(data?.injuries ?? []);
    setInitialInjuries(data?.injuries ?? []);
  }, [data?.injuries]);

  useEffect(() => {
    const initOptions = async () => {
      const expectedInput = schema?.expected_input;
      const selectedSchema = schema[expectedInput];
      const injuriesSchema = selectedSchema?.properties?.injuries.items.$ref;
      const url = schema[injuriesSchema].properties.body_part.enum;
      const queryString = url.split("?")[1];
      const params = Object.fromEntries(new URLSearchParams(queryString));
      const resp = await fieldOptionRepository.query(params);
      const sortedData = resp.sort((a, b) => a.value.localeCompare(b.value));
      setOptions((sortedData || []).map(({ key, value }) => ({ key, value })));
    };
    initOptions();
  }, [schema, fieldOptionRepository]);

  return (
    <>
      <div className="mb-4 flex w-full items-center justify-between gap-8">
        <QuestionTitle label={label}>
          <div>
            <Button
              sx={{
                color: HNSColors.secondary[500],
                border: `1px solid ${HNSColors.secondary[500]}`,
                "&:hover": {
                  background: HNSColors.secondary[900],
                  border: `1px solid ${HNSColors.secondary[500]}`,
                },
              }}
              className="w-52 rounded-3xl bg-white py-2 pl-8 pr-2 text-xs normal-case"
              variant="outlined"
              onClick={(ev) => setAnchorEl(ev.currentTarget)}
            >
              Add Injury Details
              <ExpandMore fontSize="small" className="ml-auto" />
            </Button>
            <Popover
              anchorEl={anchorEl}
              open={openMenuOpts}
              onClose={handleCloseOpts}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              slotProps={{
                paper: {
                  style: {
                    ...sharedStyles.selectMenuPaper,
                    maxHeight: 400,
                    width: 300,
                    padding: 0,
                  },
                },
              }}
            >
              <TextField
                fullWidth
                autoFocus
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  e.stopPropagation();
                  setSearchTerm(e.target.value);
                }}
                placeholder="Search body part..."
                sx={{
                  ...sharedStyles.textField,
                  mb: 1,
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#FFFFFF",
                  zIndex: 2,
                  p: 2,
                }}
              />
              {options
                .filter(
                  ({ key, value }) =>
                    !formData.some((item) => item.body_part === key) &&
                    value.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map(({ key, value }) => (
                  <MenuItem key={key} onClick={() => handleAdd(key)} sx={sharedStyles.menuItem}>
                    {value}
                  </MenuItem>
                ))}
            </Popover>
          </div>
        </QuestionTitle>
      </div>

      {formData.map((injury) => {
        const { pain_level, body_part } = injury;
        const color = renderColor(pain_level);
        const original = initialInjuries.find((item) => item.body_part === body_part);
        const initialValue = original?.pain_level ?? 0;

        return (
          <div key={body_part} className="w-full">
            <div className="flex w-full items-center gap-2 text-sm">
              <span style={{ color: HNSColors.neutrals[300] }} className="pr-2">
                <span className="font-semibold">{body_part}</span> {pain_level}
                /10
              </span>
            </div>
            <div className="flex w-full items-center gap-4">
              <Slider
                max={10}
                min={0}
                value={typeof pain_level === "number" ? pain_level : 0}
                onChange={(_, newVal) => handleChange(body_part, newVal)}
                sx={{
                  color,
                  "& .MuiSlider-rail": {
                    backgroundColor: HNSColors.sliderNotFilled,
                  },
                }}
              />
              <span>{pain_level}</span>
              <Button
                sx={{
                  color: HNSColors.secondary[500],
                  border: `1px solid ${HNSColors.secondary[500]}`,
                  "&:hover": {
                    background: HNSColors.secondary[900],
                    border: `1px solid ${HNSColors.secondary[500]}`,
                  },
                }}
                className="w-[180px] rounded-3xl px-4 py-2 text-xs normal-case"
                variant="outlined"
                disabled={!original || initialValue === pain_level}
                onClick={() => handleChange(body_part, initialValue)}
              >
                Reset
              </Button>
            </div>
          </div>
        );
      })}

      {openNotesModal && (
        <NoteModal
          closeModal={handleCloseModal}
          formData={formData}
          handleAnswerChange={handleAnswerChange}
          questionKey={questionKey}
          selectedInjury={selectedInjury}
          setFormData={setFormData}
          setSelectedInjury={setSelectedInjury}
        />
      )}
    </>
  );
};

export default Injuries;
